import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { Hash, ArrowLeft, Fire, MusicNotes } from "@phosphor-icons/react";
import StreakLeaderboard from "../components/StreakLeaderboard";
import RemixSoundCharts from "../components/RemixSoundCharts";

const TABS = [
  { id: "hashtags", label: "Hashtags", icon: Hash, eyebrow: "This week", title: "Trending hashtags" },
  { id: "streaks", label: "Streaks", icon: Fire, eyebrow: "Stay consistent", title: "Streak leaderboard" },
  { id: "remix", label: "Remix Sounds", icon: MusicNotes, eyebrow: "Weekly chart", title: "Remix sound charts" },
];

export default function Trending() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.id === params.get("tab")) || TABS[0];
  const [tags, setTags] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hashtags/trending");
      setTags(data);
    } catch (err) {
      console.error("trending load failed", err);
      setTags([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-8" data-testid="trending-page">
      <Link to="/explore" className="inline-flex items-center gap-2 text-cream/60 hover:text-cream text-xs tracking-[0.2em] uppercase font-bold mb-4">
        <ArrowLeft size={14} /> Explore
      </Link>
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">{tab.eyebrow}</p>
      <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">{tab.title}</h1>

      <div className="mt-5 flex gap-2 overflow-x-auto pb-1" data-testid="trending-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab.id;
          return (
            <button
              key={t.id}
              onClick={() => setParams(t.id === "hashtags" ? {} : { tab: t.id })}
              data-testid={`trending-tab-${t.id}`}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black tracking-[0.2em] uppercase whitespace-nowrap border transition-colors ${
                active ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10 hover:border-papaya/40"
              }`}
            >
              <Icon size={13} weight="fill" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab.id === "streaks" && <StreakLeaderboard />}
      {tab.id === "remix" && <RemixSoundCharts />}
      {tab.id === "hashtags" && (
        <>
          {tags === null && <p className="mt-6 text-cream/40 text-sm">Loading…</p>}
          {tags && tags.length === 0 && (
            <div className="mt-8 crystal-glass rounded-3xl p-6">
              <p className="text-cream font-bold">No trending tags yet.</p>
              <p className="text-cream/60 text-sm mt-1">Add a <span className="text-papaya font-black">#hashtag</span> to your next post to start a wave.</p>
            </div>
          )}
          <div className="mt-6 space-y-2" data-testid="trending-list">
            {tags && tags.map((t, i) => (
              <Link
                key={t.tag}
                to={`/tag/${t.tag}`}
                data-testid={`trending-${t.tag}`}
                className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/40 transition-colors"
              >
                <div className="w-8 text-center text-cream/60 font-black">#{i + 1}</div>
                <div className="w-11 h-11 rounded-full bg-papaya/20 flex items-center justify-center">
                  <Hash size={20} className="text-papaya" weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-cream font-black truncate">#{t.tag}</p>
                  <p className="text-cream/50 text-xs">{t.count} posts</p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
